import { projectPermissions } from '@/Models';
import Formatter from '@/Utils/Formatter';
import { convertToMajorValue } from '@/Utils/NumberUtility';
import { hasProjectPermissionCurried } from '@/Utils/RoleUtility';
import { pluralize } from '@/Utils/TextUtility';

export const performanceMarketingCampaignsTypes = {
    'crowdfunding': 'crowdfunding',
    'upcoming': 'upcoming',
    // currently used for Late Pledges and Pledge Manager
    'longTermSales': 'longTermSales',
};

export const navigationActions = {
    extendCampaign: 'extendCampaign',
    adForLast48Hours: 'adForLast48Hours',
};

export const potentialLevels = {
    High: 'High',
    Moderate: 'Moderate',
    Low: 'Low',
};

export const contentAnalysisStatuses = {
    none: 'None',
    pending: 'Pending',
    complete: 'Complete',
    failed: 'Failed',
};

export const locationRestrictedTypes = {
    unlimited: 'Unlimited',
    notAvailableIn: 'NotAvailableIn',
    availableIn: 'AvailableIn',
};

export const performanceMarketingWizardSteps = {
    info: 'info',
    budget: 'budget',
};

export const optimizationTargetTypes = {
    purchase: 'Purchase',
    addToWishlist: 'AddToWishlist',
};

export const budgetSpendingStrategyTypes = {
    linearSpread: 'LinearSpread',
    crowdfundingCampaign: 'CrowdfundingCampaign',
};

export const feeHandlingStrategyTypes = {
    staticPercentageStrategy: 'StaticPercentageStrategy',
    platformTrialStrategy: 'PlatformTrialStrategy',
};

export const campaignStatusTypes = {
    active: 'Active',
    paused: 'Paused',
    suggestion: 'Suggestion',
};

export const assetGroupStatusTypes = {
    active: 'Active',
    paused: 'Paused',
    draft: 'Draft',
};

export const entityEffectiveStatusTypes = {
    draft: 'Draft',
    active: 'Active',
    paused: 'Paused',
    scheduled: 'Scheduled',
    completed: 'Completed',
    inactive: 'Inactive',
};

export const assetReviewStatus = {
    pendingReview: 'PendingReview',
    approved: 'Approved',
    rejected: 'Rejected',
};

export const paymentStatus = {
    pending: 'Pending',
    paid: 'Paid',
    failed: 'Failed',
};

export const assetReviewStatusDisplayName = {
    [assetReviewStatus.pendingReview]: 'Awaiting Review',
    [assetReviewStatus.approved]: 'Approved',
    [assetReviewStatus.rejected]: 'Rejected',
};

export const textAssetTypes = {
    'headline': 'Headline',
    'primaryText': 'PrimaryText',
    'description': 'Description',
};

export const imageAssetTypes = {
    'horizontalImage': 'HorizontalImage',
    'squareImage': 'SquareImage',
    'verticalImage': 'VerticalImage',
    'shortVerticalImage': 'ShortVerticalImage',
};

export const videoAssetTypes = {
    'videoVertical': 'videoVertical',
    'videoSquare': 'videoSquare',
};

export const issuesCodeTypes = {
    ASSETS_PENDING_REVIEW: 'ASSETS_PENDING_REVIEW',
    ASSETS_REJECTED: 'ASSETS_REJECTED',
    LACK_OF_ASSETS: 'LACK_OF_ASSETS',
    NO_ADS: 'NO_ADS',
    LOW_FUNDS: 'LOW_FUNDS',
    NO_FUNDS: 'NO_FUNDS',
    ASSET_GROUP_READY_TO_ACTIVATE: 'ASSET_GROUP_READY_TO_ACTIVATE',
};

export const recommendationsCodeTypes = {
    INCREASE_BUDGET: 'INCREASE_BUDGET',
    CREATE_AD_FOR_LAST_48_HOURS: 'CREATE_AD_FOR_LAST_48_HOURS',
    EXTEND_ACTIVE: 'EXTEND_ACTIVE',
};

export const expectedFundingConversionFactors = {
    crowdfunding: 0.05,
    nonCrowdfunding: 0.0075,
};

export const marketingCampaignConstants = {
    MAX_DURATION_IN_DAYS: 90,
};

export const suggestionCampaignSteps = {
    suggestion: 'suggestion',
    wizard: 'wizard',
    payment: 'payment',
};

export const DEFAULT_SUGGESTION_CAMPAIGN_STEP = suggestionCampaignSteps.suggestion;

export function buildFundsEnoughForLabel(daysRemaining) {
    if (daysRemaining === null) {
        return '-';
    }
    if (daysRemaining < 1) {
        return 'less than a day';
    }
    else if (daysRemaining >= 7) {
        return 'more than 7 days';
    }

    return `${daysRemaining} ${pluralize(daysRemaining, 'day', 'days') }`;
}

export function calculateThreeDaysSpending(dailySpendingData) {
    if (!dailySpendingData || dailySpendingData.length === 0) {
        return 0;
    }
    const firstThreeDays = dailySpendingData.slice(0, 3);
    return firstThreeDays.reduce((sum, item) => sum + (item.dailySpending || 0), 0);
}

export const wizardStepsGuard = (to, from, next) => {
    if (performanceMarketingCampaignsTypes[to.params.type]) {
        next();
    }
    else {
        next({
            name: 'performance-marketing-campaigns-select',
            replace: true,
            params: to.params,
        });
    }
};

export const suggestionStepsGuard = (to, from, next) => {
    const suggestionCampaignStepsArray = Object.values(suggestionCampaignSteps);
    const step = to.params?.step;
    if (!step || suggestionCampaignStepsArray.includes(step)) {
        next();
    }
    else {
        next({
            name: 'performance-marketing-campaign-suggestion',
            params: { ...to.params, step: 'suggestion' },
            replace: true,
        });
    }
};
export function hasOnlyUnfinishedAssetGroups(assetGroups) {
    if (!assetGroups || assetGroups.length === 0) {
        return true;
    }
    return assetGroups.every(assetGroup => assetGroup.configuredStatus === assetGroupStatusTypes.draft);
}

const buildStatisticItem = ({ title, value, enabled, action, tooltip, footer, highlightClasses, iconName }) => {
    return { title, value, action, tooltip, enabled, footer, highlightClasses, iconName };
};

export const buildStatisticsItemRemainingFunds = (fundsRemaining, campaignId, currencySymbol, hasAction = true, enabled = true) => {
    const formattedValue =  Formatter.format(convertToMajorValue(fundsRemaining), 'C2', currencySymbol);
    const route = { name: 'performance-marketing-funds',  params: { campaignId } };
    return buildStatisticItem({
        title: 'remaining funds',
        value: formattedValue,
        action: hasAction ? { route, label: 'show funds' } : null,
        tooltip: 'The money available for marketing campaign. When out of funds, ads automatically stop.',
        enabled,
    });
};

export const buildStatisticsItemRemainingAddFunds = (fundsRemaining, campaignId, currencySymbol, enabled = true) => {
    const formattedValue =  Formatter.format(convertToMajorValue(fundsRemaining), 'C2', currencySymbol);
    const route = { name: 'performance-marketing-payment-add',  params: { campaignId } };
    return buildStatisticItem({
        title: 'remaining funds',
        value: formattedValue,
        action: { route, label: 'add funds' },
        tooltip: 'The money available for marketing campaign. When out of funds, ads automatically stop.',
        enabled,
    });
};

export const buildStatisticsItemFundsEnoughFor = (daysRemaining, enabled = true) => {
    const formattedValue = buildFundsEnoughForLabel(daysRemaining);
    return buildStatisticItem({
        title: 'funds enough for',
        value: formattedValue,
        tooltip: 'Estimated number of days the top-up amount will be sufficient to run ads, based on the current campaign spending dynamics.',
        enabled,
    });
};

export const buildStatisticsItemAdfoundFees = (feeAmount, campaignId, isFreeTrial, currencySymbol, enabled = true) => {
    const formattedValue = Formatter.format(convertToMajorValue(feeAmount), 'C2', currencySymbol);
    const route = { name: 'performance-marketing-billing', params: { campaignId } };
    const label = 'view billing';
    let tooltipText = 'Fees are calculated based on Adfound\'s performance. The final cost will be displayed after the campaign in the Billing section.';

    if (isFreeTrial) {
        tooltipText += ' Free trial funds used for running your ads are excluded from fee calculations.';
    }

    return buildStatisticItem({
        title: 'Adfound fees',
        value: formattedValue,
        action: { route, label },
        tooltip: tooltipText,
        enabled,
    });
};

export const buildStatisticItemOverallSpending = (totalSpent, currencySymbol, enabled = true) => {
    const formattedValue = Formatter.format(convertToMajorValue(totalSpent), 'C2', currencySymbol);
    return buildStatisticItem({
        title: 'overall spending',
        value: formattedValue,
        tooltip: 'The total amount of funds spent on ads.',
        enabled,
    });
};

export const buildStatisticsItemConversions = (conversionCount, optimizationTarget, enabled = true) => {
    const formattedValue = Formatter.format(conversionCount, 'N0');
    return buildStatisticItem({
        title: 'number of conversions',
        value: formattedValue,
        tooltip: optimizationTarget === optimizationTargetTypes.purchase ? 'Number of backers gained from your ads.' : 'Number of followers gained from your ads.',
        enabled,
    });
};

export const buildStatisticsItemCostPerFollower = (costPerConversion, currencySymbol, potentialLevel, enabled = true) => {
    const formattedValue = Formatter.format(convertToMajorValue(costPerConversion), 'C2', currencySymbol);
    const costPerFollowerItem = {
        title: 'cost per follower',
        value: formattedValue,
        tooltip: 'The amount of money spent to gain each new follower from your ads so far.',
        enabled,
    };
    const footer = {
        label: 'high potential',
        tooltip: `This campaign’s cost per follower (CPF) indicates high potential compared to similar projects.
             Consider increasing your spending to keep momentum and attract more followers.`,
    };
    if (potentialLevel === potentialLevels.High) {
        costPerFollowerItem.footer = footer;
        costPerFollowerItem.highlightClasses = '_tc--accent';
    }
    return buildStatisticItem(costPerFollowerItem);
};

export const buildStatisticsItemOverallRevenue = (totalValue, currencySymbol, enabled = true) => {
    const formattedValue = Formatter.format(convertToMajorValue(totalValue), 'C2', currencySymbol);
    return buildStatisticItem({
        title: 'overall revenue',
        value: formattedValue,
        tooltip: 'The total income generated from your marketing campaign without ad spend or fees.',
        enabled,
    });
};

export const buildStatisticsItemROAS = (roas, enabled = true, potentialLevel) => {
    const formattedValue = Formatter.format(roas, 'N2');
    const roasStatisticItem = {
        title: 'overall ROAS',
        value: formattedValue,
        tooltip: 'Return on Advertising Spend (ROAS) shows how much money you earn for each dollar or euro spent on advertising.',
        enabled,
    };
    const footer = {
        label: 'high potential',
        tooltip: `This campaign\'s return on ad spend (ROAS) shows high potential.
            Increasing your spending can help maintain growth and maximize revenue.`,
    };
    if (potentialLevel === potentialLevels.High) {
        roasStatisticItem.footer = footer;
        roasStatisticItem.highlightClasses = '_tc--accent';
    }
    return buildStatisticItem(roasStatisticItem);

};

export const buildStatisticsItemTotalCost = (totalCost, currencySymbol, enabled = true) => {
    const formattedValue = Formatter.format(convertToMajorValue(totalCost), 'C2', currencySymbol);
    return buildStatisticItem({
        title: 'total cost',
        value: formattedValue,
        tooltip: 'The total cost of your campaign, including top-ups and fees. The final fee amount will be calculated and displayed at the end of the campaign when the invoice is ready for payment.',
        enabled,
    });
};

export const buildStatisticsItemPaid = (paid, currencySymbol, enabled = true) => {
    const formattedValue = Formatter.format(convertToMajorValue(paid), 'C2', currencySymbol);
    return buildStatisticItem({
        title: 'paid',
        value: formattedValue,
        tooltip: 'The total amount of invoices that have already been paid. It\'s the sum of all payments with the Paid status.',
        enabled,
    });
};

export const buildStatisticsItemToPay = (toPay, currencySymbol, enabled = true) => {
    const formattedValue = Formatter.format(convertToMajorValue(toPay), 'C2', currencySymbol);
    return buildStatisticItem({
        title: 'to pay',
        value: formattedValue,
        tooltip: 'The total amount of invoices that await payment. It\'s the sum of all payments with the Invoiced status.',
        enabled,
    });
};

export const buildStatisticsItemFeeRate = (feeRate, enabled = true) => {
    const formattedValue = Formatter.format(feeRate, 'P0');
    return buildStatisticItem({
        title: 'fee rate',
        value: formattedValue,
        tooltip: 'Adfound fee is a percentage of the marketing campaign\'s actual revenue and will be charged after the campaign ends.',
        enabled,
    });
};

export const buildStatisticsItemDeclaredBudget = (maxSpent, currencySymbol, enabled = true) => {
    const formattedValue = Formatter.format(convertToMajorValue(maxSpent), 'C2', currencySymbol);
    return buildStatisticItem({
        title: 'declared budget',
        value: formattedValue,
        tooltip: 'This is the planned overall budget for the campaign, used to shape the ad strategy and spending pace throughout the campaign duration.',
        enabled,
    });
};

export const buildStatisticItemImpressions = (impressions, enabled = true) => {
    const formattedValue = Formatter.format(impressions, 'N0');
    return buildStatisticItem({
        title: 'impressions',
        value: formattedValue,
        tooltip: 'Total number of times ads were displayed to users.',
        enabled,
    });
};

export const buildStatisticsItemClicks = (clicks, enabled = true) => {
    const formattedValue = Formatter.format(clicks, 'N0');
    return buildStatisticItem({
        title: 'clicks',
        value: formattedValue,
        tooltip: 'Total number of times users clicked on your ads.',
        enabled,
    });
};

export const budgetExceededMessage = 'After the top-up, the campaign funds will exceed the declared budget limit. Proceeding will update the budget to prevent unspent funds and adjust the campaign dynamics accordingly. If you do not wish to extend the budget, cancel and modify the top-up amount.';

export const buildStatisticsItem = {
    overallSpending: buildStatisticItemOverallSpending,
    conversions: buildStatisticsItemConversions,
    costPerFollower: buildStatisticsItemCostPerFollower,
    overallRevenue: buildStatisticsItemOverallRevenue,
    ROAS: buildStatisticsItemROAS,
    clicks: buildStatisticsItemClicks,
    impressions: buildStatisticItemImpressions,
    totalCost: buildStatisticsItemTotalCost,
    paid: buildStatisticsItemPaid,
    toPay: buildStatisticsItemToPay,
    feeRate: buildStatisticsItemFeeRate,
    remainingFunds: buildStatisticsItemRemainingFunds,
    remainingAddFunds: buildStatisticsItemRemainingAddFunds,
    fundsEnoughFor: buildStatisticsItemFundsEnoughFor,
    adfoundFees: buildStatisticsItemAdfoundFees,
    declaredBudget: buildStatisticsItemDeclaredBudget,
};

export const questions = (contactUrl) =>
    [
        {
            id: 1,
            title: 'What should my budget be?',
            description: 'Adfound allows campaigns with a budget of minimum 25 EUR per day. Once you input the basic details into the system, it will guide you and suggest appropriate budgets for the phase you’re in. The final decision regarding budget is yours.',
            isCollapsed: true,
        },
        {
            id: 2,
            title: 'Is there a spending limit?',
            description: 'We recommend spending as much as makes sense and for as long as it remains profitable. If you’re satisfied with the ROAS (Return On Advertising Spend), consider increasing your investment. If you notice ROAS decreasing, it might be the right time to reduce spending. Always ensure your budget covers at least a few days of campaign activity.',
            isCollapsed: true,
        },
        {
            id: 3,
            title: 'How long the campaign should be?',
            description: 'The system allows you to launch a campaign for as little as one hour. However, it is recommended to run the campaign for at least three days to allow the learning phase to complete and optimize performance.',
            isCollapsed: true,
        },
        {
            id: 4,
            title: 'Is there a guaranteed ROAS?',
            description: 'Adfound uses a highly refined audience database to ensure your ads reach the right people. However, whether they choose to support your project depends on how appealing your project is to them. For this reason, we cannot guarantee a specific ROAS for your campaign.',
            isCollapsed: true,
        },
        {
            id: 5,
            title: 'What do I need to prepare for the campaign?',
            description: 'Provide Adfound with a selection of graphics and text proposals that reflect your project’s vision. Using these assets, Adfound will create optimized ad variations and manage your campaign for you.',
            isCollapsed: true,
        },
        {
            id: 6,
            title: 'What if I want to stop the ads, increase the budget, etc.?',
            description: `<div>You can set spending limits during the initial setup and adjust them at any time.<br><br>                      
                          You can pause and unpause your ads anytime. Manually pausing the ad will stop its funding. 
                          However, spending funds are automatically paused for low-performing assets, so you don't need 
                          to worry about doing it yourself.<br><br> If you want to terminate your whole campaign permanently 
                          and have your unused funds refunded or transferred to another campaign, 
                          <a href=\"${contactUrl}\" rel=\"nofollow noopener noreferrer\">
                          contact us</a>.</div>`,
            isCollapsed: true,
        },
        {
            id: 7,
            title: 'How much does it cost to work with Adfound?',
            description: 'Adfound fee is 5% of the money the ads bring. You will be charged at the end of your marketing campaign, based on the total revenue generated through Adfound. Campaigns in the "Upcoming" phase are not charged. You can monitor your spending and fees via the dashboard, with data synchronized periodically.',
            isCollapsed: true,
        },
        {
            id: 8,
            title: 'How do you determine which funds were brought by Adfound?',
            description: 'For both generating interest and revenue for your project, Adfound uses 7-day click and 1-day view attribution from Meta system.',
            isCollapsed: true,
        },
        {
            id: 9,
            title: 'Do I need a business account in Meta?',
            description: 'You don’t need to have Meta account to start running ads with Adfound, your ads will be displayed on our established accounts.',
            isCollapsed: true,
        },
        {
            id: 10,
            title: 'Do I need to set up any tracking tools or codes?',
            description: 'You don’t need to connect any tracking codes or tools. Once you launch your campaign, Adfound will handle all the tracking and measurement for you.',
            isCollapsed: true,
        },
    ];

export const benefits = [
    {
        title: 'Reach the right audience',
        icon: 'users',
        description:
            'With years of experience engaging millions of gamers, Adfound guarantees your ads reach the most relevant audience.',
    },
    {
        title: 'Invest Smarter',
        icon: 'money-bill-trend-up',
        description:
            'Adfound constantly optimizes your campaigns, ensuring that funds are allocated only to ads that drive attention and value to your project.',
    },
    {
        title: 'Save time and effort',
        icon: 'watch',
        description:
            'Set up your ads in just a few clicks using an advertising account with a long-standing, trusted reputation.',
    },
    {
        title: 'Maximize Performance',
        icon: 'analytics',
        description:
            'Simply provide your graphics and copy, and Adfound will run automated tests to identify and invest heavily in the highest-performing combinations.',
    },
    {
        title: 'Maintain control',
        icon: 'sliders-h',
        description:
            'Spend only what’s right for your campaign. Pause or boost your ads whenever needed.',
    },

    {
        title: 'Monitor results',
        icon: 'gauge-max',
        description:
            'Track your campaign’s performance with our user-friendly dashboard, offering real-time insights into key metrics.',
    },
];

export const hasAccessToPerformanceMarketing = (user, projectContext) => {
    const { managePerformanceMarketingIntegration } = projectPermissions;
    const hasProjectPermission = hasProjectPermissionCurried(
        user,
        projectContext,
    );
    return hasProjectPermission(managePerformanceMarketingIntegration);
};

export const adMediaAssetModalModes = {
    add: 'add',
    edit: 'edit',
};

export const previewImagesTypes = {
    Square: 'square',
    Vertical: 'vertical',
    Horizontal: 'horizontal',
    ShortVertical: 'shortVertical',
};

export const previewVideoTypes = {
    Vertical: 'vertical',
    Square: 'square',
};

export const previewOriginFormatTypes = {
    Feeds: 'Feeds',
    StoriesAndReels: 'Stories and Reels',
    RightColumn: 'Right column',
};

export const previewFormatTypes = {
    FacebookDesktopRightColumn: 'FacebookDesktopRightColumn',
    FacebookMobileFeed: 'FacebookMobileFeed',
    InstagramMobileFeed: 'InstagramMobileFeed',
    MetaReels: 'MetaReels',
    MetaStories: 'MetaStories',
    LegacyFacebookDesktopRightColumn: 'LegacyFacebookDesktopRightColumn',
};

export const ThumbLayoutTypes = {
    Stacked: 'Stacked',
    SideBySide: 'SideBySide',
};

export const previewFilenameKeys = {
    horizontalFilename: 'horizontalFilename',
    squareFilename: 'squareFilename',
    verticalFilename: 'verticalFilename',
    shortVerticalFilename: 'shortVerticalFilename',
};

export const previewFormats = {
    FacebookMobileFeed: {
        id: 1,
        icons: ['facebook-f'],
        text: 'mobile feed',
        type: previewImagesTypes.ShortVertical,
        name: previewFormatTypes.FacebookMobileFeed,
        originType: previewOriginFormatTypes.Feeds,
        variants: {
            [ThumbLayoutTypes.Stacked]: {
                type: previewImagesTypes.Square,
                filenameKey: previewFilenameKeys.squareFilename,
            },
            [ThumbLayoutTypes.SideBySide]: {
                type: previewImagesTypes.ShortVertical,
                filenameKey: previewFilenameKeys.shortVerticalFilename,
            },
        },
    },
    InstagramMobileFeed: {
        id: 2,
        icons: ['instagram'],
        text: 'mobile feed',
        type: previewImagesTypes.Square,
        name: previewFormatTypes.InstagramMobileFeed,
        originType: previewOriginFormatTypes.Feeds,
        variants: {
            [ThumbLayoutTypes.Stacked]: {
                type: previewImagesTypes.Square,
                filenameKey: previewFilenameKeys.squareFilename,
            },
            [ThumbLayoutTypes.SideBySide]: {
                type: previewImagesTypes.ShortVertical,
                filenameKey: previewFilenameKeys.shortVerticalFilename,
            },
        },
    },
    MetaReels: {
        id: 3,
        icons: ['facebook-f', 'instagram'],
        text: 'reels',
        type: previewImagesTypes.Vertical,
        name: previewFormatTypes.MetaReels,
        originType: previewOriginFormatTypes.StoriesAndReels,
        variants: {
            [ThumbLayoutTypes.Stacked]: {
                type: previewImagesTypes.Vertical,
                filenameKey: previewFilenameKeys.verticalFilename,
            },
            [ThumbLayoutTypes.SideBySide]: {
                type: previewImagesTypes.Vertical,
                filenameKey: previewFilenameKeys.verticalFilename,
            },
        },
    },
    MetaStories: {
        id: 4,
        icons: ['facebook-f', 'instagram'],
        text: 'stories',
        type: previewImagesTypes.Vertical,
        name: previewFormatTypes.MetaStories,
        originType: previewOriginFormatTypes.StoriesAndReels,
        variants: {
            [ThumbLayoutTypes.Stacked]: {
                type: previewImagesTypes.Vertical,
                filenameKey: previewFilenameKeys.verticalFilename,
            },
            [ThumbLayoutTypes.SideBySide]: {
                type: previewImagesTypes.Vertical,
                filenameKey: previewFilenameKeys.verticalFilename,
            },
        },
    },
    LegacyFacebookDesktopRightColumn: {
        id: 5,
        icons: ['facebook-f'],
        text: 'desktop sidebar',
        type: previewImagesTypes.Horizontal,
        name: previewFormatTypes.LegacyFacebookDesktopRightColumn,
        originType: previewOriginFormatTypes.RightColumn,
        variants: {
            [ThumbLayoutTypes.Stacked]: {
                type: previewImagesTypes.Horizontal,
                filenameKey: previewFilenameKeys.horizontalFilename,
            },
        },
    },
    FacebookDesktopRightColumn: {
        id: 6,
        icons: ['facebook-f'],
        text: 'desktop sidebar',
        type: previewImagesTypes.Square,
        name: previewFormatTypes.FacebookDesktopRightColumn,
        originType: previewOriginFormatTypes.RightColumn,
        variants: {
            [ThumbLayoutTypes.SideBySide]: {
                type: previewImagesTypes.Square,
                filenameKey: previewFilenameKeys.squareFilename,
            },
        },
    },
};

export const previewVideoFormats = {
    FacebookMobileFeed: {
        id: 1,
        icons: ['facebook-f'],
        text: 'mobile feed',
        type: previewVideoTypes.Square,
        name: previewFormatTypes.FacebookMobileFeed,
        originType: previewOriginFormatTypes.Feeds,
        filenameKey: previewFilenameKeys.squareFilename,
    },
    InstagramMobileFeed: {
        id: 2,
        icons: ['instagram'],
        text: 'mobile feed',
        type: previewVideoTypes.Square,
        name: previewFormatTypes.InstagramMobileFeed,
        originType: previewOriginFormatTypes.Feeds,
        filenameKey: previewFilenameKeys.squareFilename,
    },
    MetaReels: {
        id: 3,
        icons: ['facebook-f', 'instagram'],
        text: 'reels',
        type: previewVideoTypes.Vertical,
        name: previewFormatTypes.MetaReels,
        originType: previewOriginFormatTypes.StoriesAndReels,
        filenameKey: previewFilenameKeys.verticalFilename,
    },
    MetaStories: {
        id: 4,
        icons: ['facebook-f', 'instagram'],
        text: 'stories',
        type: previewVideoTypes.Vertical,
        name: previewFormatTypes.MetaStories,
        originType: previewOriginFormatTypes.StoriesAndReels,
        filenameKey: previewFilenameKeys.verticalFilename,
    },
};

export const adTypes = {
    last48HoursAd: 'last48HoursAd',
    newAd: 'newAd',
};

export const accountTypes = {
    business: 'Business',
    individual: 'Individual',
};

export const feedbackTypes = {
    negative: 'negative',
    positive: 'positive',
    none: 'none',
};

export const mediaAssetsSuggestionsRejectionReasons = {
    doesNotFitCampaign: 'Doesn\'t fit my campaign',
    notRelevantForAd: 'Not relevant for my ad',
    poorVisualQuality: 'Poor visual quality',
    doesNotReflectOriginalCreative: 'Doesn\'t reflect my original creative',
    textOverlaysDoNotMeetExpectations: 'Text overlays don\'t meet expectations',
    other: 'My reason isn\'t listed',
};

export const getCurrencySymbol = (currencyShortName) => {
    const userLocale = typeof navigator !== 'undefined'
    && (navigator.language
     || navigator.browserLanguage
     || navigator.userLanguage) || 'en-US';

    if (!currencyShortName) {
        return null; // Return null if no currency is provided
    }

    return (0)
        .toLocaleString(userLocale, {
            style: 'currency',
            currency: currencyShortName,
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        })
        .replace(/\d/g, '')
        .trim();
};

export function deriveCampaignType(spendingStrategy, optimizationTarget) {
    if (optimizationTarget === optimizationTargetTypes.purchase
        && spendingStrategy === budgetSpendingStrategyTypes.crowdfundingCampaign) {

        return performanceMarketingCampaignsTypes.crowdfunding;
    }

    if (optimizationTarget === optimizationTargetTypes.purchase
        && spendingStrategy === budgetSpendingStrategyTypes.linearSpread) {

        return performanceMarketingCampaignsTypes.longTermSales;
    }

    if (optimizationTarget === optimizationTargetTypes.addToWishlist
        && spendingStrategy === budgetSpendingStrategyTypes.linearSpread) {

        return performanceMarketingCampaignsTypes.upcoming;
    }
}

export const SupportedImageFormats = {
    Horizontal: 'Horizontal',
    ShortVertical: 'ShortVertical',
    Vertical: 'Vertical',
    Square: 'Square',
};